import { LoginPageService } from "../services/login-page.service";
import { ChangeDetectorRef, Component, effect, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { FormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { UserService } from "../services/user/user.service";
import { AuthService } from "../services/auth/auth.service";
import { CartAttributes } from "@models/cart.model";
import { RouteTrackingService } from "../services/route-tracking/route-tracking.service";
import { CartItemService } from "../services/cart-item/cart-item.service";
import { ErrorStateMatcher } from "@angular/material/core";

type Timeout = ReturnType<typeof setTimeout>;
@Component({
    selector: "app-login-page",
    templateUrl: "./login-page.component.html",
    styleUrls: ["./login-page.component.scss"],
    standalone: false,
})
export class LoginPageComponent implements OnInit {
    public mobileNumber: string = "";
    public otp!: number;
    public showOTPField = false;
    public showSubmitButton = false;
    public showResendLink = false;
    public showTimer = false;
    public minutes = 5;
    public seconds = 0;
    public loginForm!: FormGroup;
    public displayTimer: string = "";
    public otpSentMessage: string = "";
    public timerInterval: Timeout | undefined;
    public otpSent = false;
    public oldCartItems: CartAttributes[] = [];
    public isSubmitting: boolean = false;
    public otpFailedMessage!: string;

    public isUserlogin = false;
    matcher = new ErrorStateMatcher();
    @ViewChild("otpInput") otpInput!: ElementRef;

    constructor(
        private formBuilder: FormBuilder,
        private loginPageService: LoginPageService,
        private router: Router,
        private userService: UserService,
        private authService: AuthService,
        private routeTrackingService: RouteTrackingService,
        private cartItemService: CartItemService,
    ) {}

    ngOnInit() {
        this.loginForm = this.formBuilder.group({
            mobileNumber: ["", [Validators.required, Validators.pattern("^[0-9]{10}$")]],
            otp: [""],
            agreeTerms: [false, Validators.requiredTrue],
            isWhatsappOtp: [true]
        });

        this.loginForm.get("mobileNumber")?.valueChanges.subscribe((value) => {
            this.loginForm.get("mobileNumber")?.setValue(this.filterNumeric(value), { emitEvent: false });
        });
        this.loginForm.get("otp")?.valueChanges.subscribe((value) => {
            this.loginForm.get("otp")?.setValue(this.filterNumeric(value), { emitEvent: false });
        });

        this.loginForm.get("mobileNumber")?.valueChanges.subscribe(() => {
            this.loginForm.get("otp")?.clearValidators();
            this.loginForm.get("otp")?.setValue("");
            this.loginForm.get("otp")?.updateValueAndValidity();

            this.showOTPField = false;
            this.showSubmitButton = false;
            this.showResendLink = false;
            this.showTimer = false;
        });
    }

    ngOnDestroy(): void {
        clearInterval(this.timerInterval);
    }

    private filterNumeric(value: string): string {
        return value.replace(/\D/g, "");
    }

    public sendOTP(): void {
        // Do NOT submit if resend link is visible (means OTP expired)
        if (this.showResendLink) return;
        const isWhatsapp = this.loginForm.value.isWhatsappOtp;

        if (this.loginForm.invalid) {
            this.loginForm.markAllAsTouched();
            return;
        }
        const requestBody = {
            mobileNumber: this.loginForm.value.mobileNumber,
            isWhatsappOtp: this.loginForm.value.isWhatsappOtp
        };
        this.isSubmitting = true;
        this.loginPageService.sendOTP(requestBody).subscribe({
            next: (res) => {
                this.isSubmitting = false;
                this.showOTPField = true;
                this.showResendLink = false;
                this.showTimer = true;
                this.startTimer();
            },
            error: (err) => {
                this.isSubmitting = false;
            },
        });
    }

    public submitOTP(): void {
        const verifyBody = {
            mobileNumber: this.loginForm.value.mobileNumber,
            otp: this.loginForm.value.otp,
        };
        this.loginPageService.verifyOtp(verifyBody).subscribe({
            next: (res) => {
                if (!res.success) {
                    this.loginForm.get("otp")?.setErrors({
                        customError: res.message,
                    });
                    this.isSubmitting = false;
                    this.otpFailedMessage = res.message!;
                    return;
                } else if (res.success) {
                    const token = res.token!;
                    localStorage.setItem("token", token);
                    this.authService.checkLoginStatus();
                    this.cartItemService.processCartItems(true);
                    this.loginForm.get("otp")?.setErrors(null);

                    this.userService.isUserDetailsExist().subscribe((response) => {
                        if (response) {
                            this.routeTrackingService.redirectingPrevioulyUrl();
                        } else {
                            this.router.navigate(["/userDetails"]);
                        }
                    });
                }
            },
            error: (err) => {
                throw new Error();
            },
        });
    }

    /** Checking OTP Input Field **/
    public isInputFieldInValid(): boolean {
        const otp = this.loginForm.get("otp")?.value;
        return !otp || otp.length !== 6;
    }

    public resendOTP(): void {
        const requestBody = {
            mobileNumber: this.loginForm.value.mobileNumber,
            isWhatsappOtp: this.loginForm.value.isWhatsappOtp
        };
        this.loginPageService.resendOtp(requestBody).subscribe(
            (next) => {
                this.showTimer = true;
                this.minutes = 1;
                this.startTimer();
                this.showResendLink = false;
                this.isSubmitting = false;
            },
            (error) => {
                // Handle error here
                console.error("Resend OTP failed", error);
                this.isSubmitting = false;
            },
        );
    }

    onOtpChange(otp: string): void {
        // If OTP input is cleared or empty, reset the form control and stop further processing.
        if (!otp) {
            this.loginForm.get("otp")?.setValue("");
            return;
        }
        this.loginForm.get("otp")?.setValue(otp);
    }

    get maskedMobile(): string {
        const mobile = this.loginForm.value.mobileNumber;
        return mobile ? `XXXXXX${mobile.slice(-4)}` : "";
    }
    public startTimer(): void {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }

        this.minutes = 5;
        this.seconds = 0;

        this.timerInterval = setInterval(() => {
            if (this.minutes === 0 && this.seconds === 0) {
                clearInterval(this.timerInterval);
                this.showTimer = false;
                this.showResendLink = true;
                return;
            }
            if (this.seconds === 0) {
                this.minutes--;
                this.seconds = 59;
            } else {
                this.seconds--;
            }
        }, 1000);
    }

    get timerDuration(): string {
        const min = this.minutes > 9 ? `${this.minutes}` : `0${this.minutes}`;
        const sec = this.seconds > 9 ? `${this.seconds}` : `0${this.seconds}`;
        return `${min}:${sec}`;
    }
}
